import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Checkbox,
  Button,
  IconButton,
  Chip,
  Alert as MuiAlert,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  NotificationsActive as AlertIcon,
  Thermostat as TempIcon,
  WaterDrop as HumidityIcon,
  Scale as WeightIcon,
  VolumeUp as SoundIcon,
  DeviceThermostat as GenericSensorIcon,
  CheckCircleOutline as AckIcon,
  TaskAlt as ResolveIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchAlerts,
  deleteAlerts,
  markAlertsAsRead,
  setSelectedAlerts,
  toggleAlertSelection,
  selectAllAlerts,
  clearSelectedAlerts,
  setSearch
} from '@store/slices/alertsSlice'
import { fetchAlertRules } from '@store/slices/alertRulesSlice'
import { Alert } from '@types'
import { formatDistanceToNow, parseISO } from 'date-fns'

// Friendly labels/icons/units per alert type. Alerts don't carry the
// triggering sensor directly (only via their rule), so we infer sensor
// "flavor" from alert_type where possible and fall back to a generic
// wording otherwise.
const ALERT_TYPE_META: Record<
  Alert['alert_type'],
  { icon: React.ReactNode; verb: string }
> = {
  threshold_high: { icon: <TempIcon />, verb: 'went above the safe range' },
  threshold_low: { icon: <WeightIcon />, verb: 'dropped below the safe range' },
  rate_change: { icon: <TempIcon />, verb: 'changed suddenly' },
  no_data: { icon: <GenericSensorIcon />, verb: 'stopped reporting data' },
  sensor_error: { icon: <GenericSensorIcon />, verb: 'reported a sensor error' },
  device_offline: { icon: <GenericSensorIcon />, verb: 'went offline' },
  low_battery: { icon: <GenericSensorIcon />, verb: 'has a low battery' },
}

const SEVERITY_META: Record<
  Alert['severity'],
  { label: string; color: 'error' | 'warning' | 'info' }
> = {
  critical: { label: 'Critical', color: 'error' },
  high: { label: 'High', color: 'error' },
  medium: { label: 'Medium', color: 'warning' },
  low: { label: 'Low', color: 'info' },
}

/** Guess a sensor icon + unit from the alert's rule name / title, since the
 * measurement type isn't included on the Alert itself. */
const guessSensorFlavor = (text: string): { icon: React.ReactNode; unit: string } => {
  const lower = text.toLowerCase()
  if (lower.includes('temp')) return { icon: <TempIcon fontSize="small" />, unit: '°C' }
  if (lower.includes('humid')) return { icon: <HumidityIcon fontSize="small" />, unit: '%' }
  if (lower.includes('weight')) return { icon: <WeightIcon fontSize="small" />, unit: 'kg' }
  if (lower.includes('sound') || lower.includes('noise')) return { icon: <SoundIcon fontSize="small" />, unit: 'dB' }
  return { icon: <GenericSensorIcon fontSize="small" />, unit: '' }
}

/** Build a plain-language description of what happened, instead of the
 * backend's technical message (rule name + raw comparison operator). */
const describeAlert = (alert: Alert): string => {
  const meta = ALERT_TYPE_META[alert.alert_type]
  const ruleName = alert.alert_rule_name || 'A monitored value'
  const { unit } = guessSensorFlavor(`${alert.title} ${ruleName}`)

  if (alert.trigger_value != null && alert.threshold_value != null) {
    const value = alert.trigger_value.toFixed(1)
    const threshold = alert.threshold_value.toFixed(1)
    const suffix = unit ? ` ${unit}` : ''
    return `${ruleName} ${meta?.verb || 'triggered'}: reached ${value}${suffix} (limit: ${threshold}${suffix}).`
  }

  return alert.message || `${ruleName} ${meta?.verb || 'triggered an alert'}.`
}

const Alerts: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const {
    alerts,
    selectedAlerts,
    loadingState,
    error,
    search
  } = useAppSelector((state) => state.alerts)
  const { alertRules, loadingState: alertRulesLoadingState } = useAppSelector((state) => state.alertRules)
  const { hives } = useAppSelector((state) => state.locations)

  const [showLoadingIcon, setShowLoadingIcon] = useState(false)

  useEffect(() => {
    if (loadingState === 'idle') {
      dispatch(fetchAlerts())
    }
    if (alertRulesLoadingState === 'idle') {
      dispatch(fetchAlertRules())
    }
  }, [dispatch, loadingState, alertRulesLoadingState])

  // Filter alerts based on search
  const filteredAlerts = alerts.filter((alert) => {
    if (search && !getHiveName(alert.hive).toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    return true
  })

  const allFilteredChecked = filteredAlerts.length > 0 &&
    filteredAlerts.every(alert => selectedAlerts.includes(alert.id))

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearch(event.target.value))
  }

  const handleClearSearch = () => {
    dispatch(setSearch(''))
  }

  const handleToggleAll = () => {
    if (allFilteredChecked) {
      dispatch(clearSelectedAlerts())
    } else {
      dispatch(setSelectedAlerts(filteredAlerts.map(alert => alert.id)))
    }
  }

  const handleToggleAlert = (alertId: number) => {
    dispatch(toggleAlertSelection(alertId))
  }

  const handleResolveSelected = async () => {
    if (selectedAlerts.length === 0) return

    const confirmMessage = selectedAlerts.length === alerts.length
      ? 'Mark all alerts as resolved? This clears them from your active list.'
      : `Mark ${selectedAlerts.length} selected alert(s) as resolved?`

    if (window.confirm(confirmMessage)) {
      setShowLoadingIcon(true)
      await dispatch(deleteAlerts(selectedAlerts))
      setShowLoadingIcon(false)
    }
  }

  const handleAcknowledgeSelected = async () => {
    if (selectedAlerts.length === 0) return
    await dispatch(markAlertsAsRead(selectedAlerts))
  }

  const handleAcknowledgeOne = async (alertId: number) => {
    await dispatch(markAlertsAsRead([alertId]))
  }

  const handleResolveOne = async (alertId: number) => {
    await dispatch(deleteAlerts([alertId]))
  }

  const handleManageRules = () => {
    navigate('/alertrules')
  }

  const getHiveName = (hiveId: number | null): string => {
    if (!hiveId) return 'Unknown hive'
    const hive = hives.find(h => h.id === hiveId)
    return hive?.name || `Hive ${hiveId}`
  }

  const getFriendlyTime = (dateString: string): string => {
    try {
      return formatDistanceToNow(parseISO(dateString), { addSuffix: true })
    } catch {
      return ''
    }
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  const showPlaceholder = alerts.length === 0 && alertRules.length === 0

  return (
    <Box>
      {/* Filter Bar */}
      {!showPlaceholder && (
        <Box
          sx={{
            backgroundColor: theme.palette.SmartNyuki?.orangeLight || '#fffaf0',
            borderBottom: `1px solid ${theme.palette.SmartNyuki?.orangeBorder || '#fff5e2'}`,
            py: 1,
            mb: { xs: 1.5, md: 2 },
          }}
        >
          <Container>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Select All Checkbox */}
              <Tooltip title="Select all">
                <Checkbox
                  checked={allFilteredChecked}
                  indeterminate={selectedAlerts.length > 0 && !allFilteredChecked}
                  onChange={handleToggleAll}
                  size="small"
                />
              </Tooltip>

              {/* Search Field */}
              <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                <TextField
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by hive name..."
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedAlerts.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AckIcon />}
                      onClick={handleAcknowledgeSelected}
                    >
                      {isMobile ? 'Seen' : `Mark as Seen (${selectedAlerts.length})`}
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={showLoadingIcon ? <CircularProgress size={16} /> : <ResolveIcon />}
                      onClick={handleResolveSelected}
                      disabled={showLoadingIcon}
                    >
                      {isMobile ? 'Resolve' : `Resolve (${selectedAlerts.length})`}
                    </Button>
                  </>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={handleManageRules}
                >
                  {isMobile ? 'Rules' : 'Alert Rules'}
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Container sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 3, md: 4 }, px: { xs: 1.5, md: 3 } }}>
        {error && (
          <MuiAlert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
            We couldn't load your alerts. Please try refreshing the page.
          </MuiAlert>
        )}

        {showPlaceholder ? (
          // Placeholder for no alerts
          <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
            <AlertIcon sx={{ fontSize: { xs: 48, md: 64 }, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              No Alert Rules Set Up Yet
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: { xs: 2, md: 4 }, fontSize: { xs: '0.875rem', md: '1rem' } }}>
              Set up alert rules to get notified automatically when a hive needs attention —
              for example, if it gets too hot or loses weight suddenly.
            </Typography>
            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<SettingsIcon />}
              onClick={handleManageRules}
            >
              Set Up Your First Alert
            </Button>
          </Box>
        ) : alerts.length === 0 ? (
          // No active alerts
          <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
            <AlertIcon sx={{ fontSize: { xs: 48, md: 64 }, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              All Clear!
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: { xs: 2, md: 4 }, fontSize: { xs: '0.875rem', md: '1rem' } }}>
              No active alerts for your hives. Your colonies are doing well.
            </Typography>
            <Button
              variant="outlined"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<SettingsIcon />}
              onClick={handleManageRules}
            >
              Manage Alert Rules
            </Button>
          </Box>
        ) : (
          // Alerts List
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {filteredAlerts.map((alert) => {
              const severity = SEVERITY_META[alert.severity] || SEVERITY_META.medium
              const typeMeta = ALERT_TYPE_META[alert.alert_type]
              const isNew = !alert.acknowledged

              return (
                <Grid item xs={12} key={alert.id}>
                  <Card
                    sx={{
                      borderLeft: `5px solid ${theme.palette[severity.color].main}`,
                      backgroundColor: isNew ? `${theme.palette[severity.color].main}0d` : 'background.paper',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Checkbox
                          checked={selectedAlerts.includes(alert.id)}
                          onChange={() => handleToggleAlert(alert.id)}
                          size="small"
                          sx={{ mt: -0.5 }}
                        />

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: `${severity.color}.main`,
                            mt: 0.5,
                          }}
                        >
                          {typeMeta?.icon || <AlertIcon />}
                        </Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {getHiveName(alert.hive)}
                            </Typography>
                            <Chip
                              label={severity.label}
                              size="small"
                              color={severity.color}
                              variant={isNew ? 'filled' : 'outlined'}
                            />
                            {isNew && (
                              <Chip label="New" size="small" color="error" variant="filled" />
                            )}
                            {alert.acknowledged && !alert.resolved_at && (
                              <Chip label="Seen" size="small" variant="outlined" />
                            )}
                          </Box>

                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            {describeAlert(alert)}
                          </Typography>

                          <Typography variant="caption" color="textSecondary">
                            {getFriendlyTime(alert.date)}
                          </Typography>
                        </Box>

                        {/* Quick actions */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {!alert.acknowledged && (
                            <Tooltip title="Mark as seen">
                              <IconButton size="small" onClick={() => handleAcknowledgeOne(alert.id)}>
                                <AckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Resolve">
                            <IconButton size="small" color="success" onClick={() => handleResolveOne(alert.id)}>
                              <ResolveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        )}

        {/* No Results */}
        {!showPlaceholder && alerts.length > 0 && filteredAlerts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="textSecondary">
              No alerts match your search
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Try adjusting your search terms
            </Typography>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
            >
              Clear Search
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default Alerts
